<template>
  <div class="project-create">
    <div class="view container">
      <HeaderComponent :title="$metaInfo.title" :breadcrumb="breadcrumb" />
      <Pricing
        tag="form"
        :value="['project', ...addons]"
        :options="{ units: units || 9 }"
        class="project-create__form"
        @submit.prevent="submit"
      >
        <template #default>
          <div class="project-create__inputs">
            <InputComponent
              v-model="data.name"
              data-field="name"
              :label="labels.name"
              required
              :is-valid="validator.fields.name.valid"
              :validation-message="validator.fields.name.error"
            />
            <InputComponent
              v-if="segments.length > 1"
              data-field="segment"
              :label="labels.segment"
            >
              <Radio
                v-model="data.segment"
                class="elder-input__element"
                :items="segments"
              />
            </InputComponent>
            <InputComponent
              v-if="data.segment === 'commercial'"
              data-field="saleTypes"
              :label="labels.saleTypes"
            >
              <div class="elder-input__element row wrap g-2">
                <CheckboxComponent
                v-model="data.saleTypes"
                v-for="saleType in saleTypes"
                :key="saleType.value"
                :label="saleType.label"
                :state="saleType.value"
              />
              </div>
            </InputComponent>
            <InputComponent
              v-if="data.segment === 'private'"
              v-model.number="units"
              data-field="units"
              :label="labels.units"
              required
              :is-valid="validator.fields.units.valid"
              :validation-message="validator.fields.units.error"
            />
            <InputComponent v-if="data.segment === 'private'" :label="labels.modules" data-field="addons">
              <template #default>
                <div class="project-create__included-addons-wrapper">
                  <CheckboxComponent
                    :sublabel="$t(`addon:project:upcoming-presentation:sublabel`)"
                    :value="true"
                    disabled
                  >
                    <div class="row bottom">
                      <span class="mr-1">{{ $t('addon:project:upcoming-presentation') }}</span>
                      <HelperPopover
                        template="project:upcoming-presentation"
                        trigger="hover"
                        delay="300"
                        class="mr-1 grow-1"
                      />
                      <Badge>{{ $t('included') }}</Badge>
                    </div>
                  </CheckboxComponent>
                  <CheckboxComponent
                    :label="$t('addon:project:residential-account')"
                    :sublabel="$t(`addon:project:residential-account:sublabel`)"
                    :value="true"
                    disabled
                  >
                    <span class="mr-1">{{ $t('addon:project:residential-account') }}</span>
                    <HelperPopover template="project:residential-account" trigger="hover" delay="300" class="mr-1" />
                  </CheckboxComponent>
                </div>

                <CheckboxComponent
                  v-model="addons"
                  v-for="addon in availableAddons"
                  :key="addon"
                  :state="addon"
                  v-tooltip.bottom-left="getAddonTooltip(addon)"
                  :disabled="getAddonDisabled(addon)"
                  :sublabel="$t(`addon:${addon}:sublabel`)"
                >
                  <span class="mr-1">{{ $t('addon:' + addon) }}</span>
                  <HelperPopover :template="addon" trigger="hover" delay="300" />
                </CheckboxComponent>
              </template>
            </InputComponent>
            <InputComponent v-if="profiles.length" :label="labels.profile" data-field="profile">
              <template #default>
                <Select v-model="data.profile">
                  <option :value="null" style="opacity: 0.5">{{ $t('none') }}</option>
                  <option v-for="profile in profiles" :key="profile.id" :value="profile.id">{{ profile.name }}</option>
                </Select>
              </template>
            </InputComponent>

            <Billing v-model="data.billing" :validator="validator" />
          </div>
        </template>
        <template #actions="{ disabled }">
          <ButtonComponent
            :label="$t('orderItem', { item: '' })"
            :icon="['fal', 'plus']"
            theme="primary"
            confirm
            type="submit"
            :disabled="!validator.passes || disabled"
            :promise="promises.mutate"
          />
        </template>
      </Pricing>
    </div>
  </div>
</template>

<script>
import { InputComponent } from 'vue-elder-input'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { ChoiceComponent } from 'vue-elder-choice'
import HeaderComponent from '@/components/Header'
import Card from '@/components/Card'
import Validator from '@/mixins/validator-v2'
import Model from '@/mixins/Model'
import Badge from '@/components/Badge'
import Pricing from '@/components/Pricing'
import { mapState, mapActions } from 'vuex'
import Store from '@/store'
import { mapi18n } from '@/i18n'
import AddressComponent from '@/components/Address'
import Billing from '@/components/Billing/Component'
import HelperPopover from '@/components/HelperPopover'
import Checklist from '@/components/Select/Checklist'
import Select from '@/components/Select/Select'
import Radio from '@kvass/radio'

const DefaultSegment = Store.state.enums.ProjectSegments.at(0) || 'private'
const DefaultSaleTypes = DefaultSegment === 'commercial' ? ['rent'] : Store.state.enums.ResidentialSaleType
const DefaultProfile = Store.state.enums.Profiles.find(p => p.default) && Store.state.enums.Profiles.find(p => p.default).id

export default {
  mixins: [
    Model({
      source: null,
      model: {
        name: null,
        segment: DefaultSegment,
        saleTypes: DefaultSaleTypes,
        billing: null,
        profile: DefaultProfile
      },
      guard: true
    }),
    Validator({
      rules: 'rules',
      labels: 'labels',
      data: 'dataComp',
    }),
  ],
  data() {
    return {
      addons: [],
      units: null,
    }
  },
  watch: {
    addons(val) {
      if (val.includes('project:shop-purchase') && !val.includes('project:shop')) this.addons.push('project:shop')
      if (val.includes('project:presentation') && val.includes('project:flatfinder')) {
        let index = this.addons.findIndex(i => i === 'project:flatfinder')
        if (!~index) return
        this.addons.splice(index, 1)
      }
    },
    availableAddons(val) {
      this.addons = this.addons.filter(a => val.includes(a))
    },
    'data.segment': {
      handler(val) {
        if (!this.data) return
        if (val ==='private') return

        this.addons = ['project:presentation']
        this.data.saleTypes = ['rent']
        this.units = 50
      },
      immediate: true,
    }
  },
  computed: {
    ...mapState('Project', ['promises', 'item']),
    ...mapi18n('labels', [
      { name: 'projectName' },
      { segment: 'project-segment' },
      { saleTypes: 'saleType' },
      { units: 'howManyUnitsOnProject' },
      { modules: ['addon', 2] },
      'size',
      { profile: ['profile', 1] },
      { billing: 'billing' },
      { 'billing.company': 'company' },
      { 'billing.email': 'email' },
      { 'billing.organizationNumber': 'orgNo' },
    ]),
    rules() {
      let base = {
        name: 'required',
        segment: 'required',
        units: 'required|min:1',
      }

      if (this.data.billing) {
        base = {
          ...base,
          'billing.company': 'required',
          'billing.organizationNumber': 'required',
          'billing.email': 'required',
        }
      }

      return base
    },
    dataComp() {
      return {
        units: this.units,
        ...this.data,
      }
    },
    breadcrumb() {
      return [{ title: this.$tc('project', 2), action: { name: 'projects' } }, { title: this.$metaInfo.title }]
    },
    profiles() {
      return (this.$store.state.enums.Profiles || []).filter(i => i.type === 'project')
    },
    availableAddons() {
      let base = ['project:presentation', 'project:bid', 'project:purchase', 'project:shop', 'project:shop-purchase']
      // if (this.units > 1) base.splice(1, 0, 'project:flatfinder')
      return base.filter(b => !this.$store.state.enums.DisabledFeatures.includes(b))
    },
    segments() {
      return this.$store.state.enums.ProjectSegments.map(value => ({ value, label: this.$t(`project-segment-${value}`) }))
    },
    saleTypes() {
      return this.$store.state.enums.ResidentialSaleType.map(value => ({ value, label: this.$t(`${value}`) }))
    }
  },
  methods: {
    ...mapActions('Project', ['mutate']),
    submit() {
      this.mutate([
        { data: this.data, units: this.units, addons: this.addons, profile: this.profile },
        { transformPayload: false },
      ]).then(data => {
        const query = {}

        if (this.addons.includes('project:presentation')) query.action = '/api/action/preset/example-data'

        this.$router.replace({
          name: 'project-edit',
          params: { id: data.id },
          query,
        })
        return data
      })
    },
    getAddonDisabled(addon) {
      switch (addon) {
        case 'project:shop':
          return this.addons.includes('project:shop-purchase')
        case 'project:flatfinder':
          return this.addons.includes('project:presentation')
        default:
          return false
      }
    },
    getAddonTooltip(addon) {
      switch (addon) {
        case 'project:shop':
          if (this.addons.includes('project:shop-purchase')) return { content: this.$t('ShopPurchaseRequiresShop') }
          return
        case 'project:flatfinder':
          if (this.addons.includes('project:presentation'))
            return {
              content: this.$t('addonIsIncludedIn', {
                addon: `"${this.$t('addon:project:flatfinder')}"`,
                in: `"${this.$t('addon:project:presentation')}"`,
              }),
            }
          return
        default:
          return
      }
    },
    async chat(value, tag, project) {
      Intercom('showNewMessage', `${tag ? tag + ' - ' : ''}${this.$t('upsaleRequestTemplate', [value])} - ${project}`)

      let sent = false,
        counter = 0

      while (!sent && counter < 10) {
        counter++
        await Wait(500)
        try {
          let btn = document
            .querySelector('.intercom-app iframe')
            .contentWindow.document.querySelector('.intercom-composer-send-button')

          if (btn) {
            btn.click()
            sent = true
          }
        } catch (err) {}
      }
    },
  },
  metaInfo() {
    return {
      title: this.$t('createProject'),
    }
  },
  components: {
    InputComponent,
    CheckboxComponent,
    HeaderComponent,
    ChoiceComponent,
    Card,
    Pricing,
    AddressComponent,
    Billing,
    HelperPopover,
    Checklist,
    Select,
    Badge,
    Radio
  },
}
</script>

<style lang="scss">
.project-create {
  $logo-size: 250px;
  $logo-offset: 5;

  background-color: $background-grey;
  padding-bottom: 0;

  &__additional-fields {
    .elder-input__field {
      border: none;
    }
    &-fields {
      display: flex;
      align-items: center;
      gap: 1rem;

      .elder-button {
        font-size: 0.8em;
        border-radius: 20px;
      }
    }
  }

  &__included-addons-wrapper {
    background-color: #fcfcfc;
    padding: inherit;
    margin: -1rem -1rem 0;
    border-radius: var(--border-radius-sm) var(--border-radius-sm) 0 0;
    gap: inherit;
    display: inherit;
    flex-direction: inherit;
    border-bottom: 1px solid var(--border-color);

    .elder-checkbox__label {
      flex: 1;
    }
  }

  &__header {
    width: 100%;
    max-width: 450px;
    margin-top: 1rem;
    border-radius: var(--border-radius);
    overflow: hidden;

    &-video {
      width: 100%;
      position: relative;
      height: 275px;
      background-color: rgba(black, 0.04);
    }

    &-info {
      font-size: 0.8em;
      opacity: 0.6;
      margin-top: 0.75rem;
      display: block;
      text-align: center;
    }
  }

  &__form {
    margin: 0 auto;
    max-width: 700px;
  }

  &__summary {
    flex-grow: 1;

    &-value {
      font-size: 1.5rem;
      font-weight: bold;
    }
  }

  &__inputs {
    display: grid;
    grid-gap: 1rem;
  }

  [data-field='addons'] {
    .elder-input__value {
      flex-direction: column;
      padding: 1rem;
      gap: 1rem;
    }
  }

  [data-field='size'],
  [data-field='sale'] {
    .elder-input__field {
      border: none;
    }

    .elder-choices__item-label span {
      display: block;

      &:last-child {
        opacity: 0.7;
        font-size: 0.8em;
        margin-top: 3px;
      }
    }
  }
}
</style>
